import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import {
  EXPIRY_THRESHOLD_PRESET_MONTHS,
  formatExpiryThresholdLabel,
  monthsToThresholdDays,
  parseExpiryThresholdMonths,
} from "../utils/medicineExpiryThreshold";
import { usePharmacyExpiryThreshold } from "../hooks/usePharmacyExpiryThreshold";

const canManagePharmacySettings = (roleName) =>
  ["pharmacy admin", "admin", "superadmin"].includes(roleName);

const PharmacyExpiryThresholdSettings = () => {
  const { user } = useAuth();
  const { months, loading, saveThreshold, thresholdDays } = usePharmacyExpiryThreshold();
  const [mode, setMode] = useState("preset");
  const [presetMonths, setPresetMonths] = useState(3);
  const [customMonths, setCustomMonths] = useState("3");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (loading) return;
    const isPreset = EXPIRY_THRESHOLD_PRESET_MONTHS.includes(months);
    if (isPreset) {
      setMode("preset");
      setPresetMonths(months);
    } else {
      setMode("custom");
      setCustomMonths(String(months));
    }
  }, [months, loading]);

  if (!canManagePharmacySettings(user?.roleName)) {
    return null;
  }

  const handleSave = async () => {
    const valueToSave =
      mode === "preset"
        ? presetMonths
        : parseExpiryThresholdMonths(customMonths, null);

    if (valueToSave == null) {
      toast.error("Enter a valid month value (e.g. 1.5), between 0.1 and 60.");
      return;
    }

    setSaving(true);
    try {
      await saveThreshold(valueToSave);
      toast.success(
        `Near-expiry threshold set to ${formatExpiryThresholdLabel(valueToSave)} (${monthsToThresholdDays(valueToSave)} days).`
      );
    } catch (error) {
      console.error("Failed to save pharmacy expiry threshold:", error);
      toast.error("Could not save setting. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Near-expiry threshold (global)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Medicines expiring within this window are marked as low / near-expiry stock in
        inventory and when issuing from pharmacy. Presets: 1, 2, or 3 months, or enter a
        custom value (e.g. 1.5).
      </Typography>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={mode}
          onChange={(_, value) => value && setMode(value)}
        >
          <ToggleButton value="preset">1 / 2 / 3 months</ToggleButton>
          <ToggleButton value="custom">Custom months</ToggleButton>
        </ToggleButtonGroup>

        {mode === "preset" ? (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={presetMonths}
            onChange={(_, value) => value != null && setPresetMonths(value)}
          >
            {EXPIRY_THRESHOLD_PRESET_MONTHS.map((m) => (
              <ToggleButton key={m} value={m}>
                {m} {m === 1 ? "month" : "months"}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        ) : (
          <TextField
            size="small"
            label="Months"
            value={customMonths}
            onChange={(e) => {
              const value = e.target.value;
              if (value === "" || /^\d*\.?\d*$/.test(value)) {
                setCustomMonths(value);
              }
            }}
            helperText="Example: 1.5 for one and a half months"
            sx={{ width: 200 }}
            inputProps={{ min: 0.1, max: 60, step: 0.1 }}
          />
        )}

        <Button variant="contained" onClick={handleSave} disabled={saving || loading}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </Box>

      {!loading && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Active threshold: <strong>{formatExpiryThresholdLabel(months)}</strong> (
          {thresholdDays} days). Used for inventory highlights and medicine issue (FEFO
          batch near expiry triggers low-stock alert when supported by backend).
        </Alert>
      )}
    </Paper>
  );
};

export default PharmacyExpiryThresholdSettings;
